import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "@/lib/auth";
import { fetchCategories } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import ImageInput from "@/components/shared/ImageInput";
import { Loader2, Save, Plus, X, ArrowLeft, Clock } from "lucide-react";
import { toast } from "sonner";

export default function MerchantProductEdit() {
    const navigate = useNavigate();
    const { productId } = useParams();
    const isEdit = Boolean(productId);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState(null);
    const [categories, setCategories] = useState([]);
    // Por defecto asumimos revisión: es lo prudente si la consulta falla.
    const [requiresApproval, setRequiresApproval] = useState(true);

    const [form, setForm] = useState({
        name: "",
        slug: "",
        shortDescription: "",
        description: "",
        price: "",
        originalPrice: "",
        images: [""],
        category: "moda",
        tags: "",
        stock: 10,
        isNew: true,
        isFeatured: false,
        variants: [], // [{name, options:[]}]
        weight: "1",
        dimLength: "",
        dimWidth: "",
        dimHeight: "",
    });

    useEffect(() => {
        fetchCategories()
            .then((cats) => setCategories(Array.isArray(cats) ? cats : []))
            .catch(() => setCategories([]));
        apiFetch
            .get("/merchant/store/readiness")
            .then(({ data }) => setRequiresApproval(data?.requiresApproval !== false))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!isEdit) return;
        apiFetch
            .get("/merchant/products")
            .then(({ data }) => {
                const p = data.find((x) => x.id === productId);
                if (p) {
                    setForm({
                        name: p.name,
                        slug: p.slug,
                        shortDescription: p.shortDescription,
                        description: p.description,
                        price: String(p.price),
                        originalPrice: p.originalPrice ? String(p.originalPrice) : "",
                        images: p.images?.length ? p.images : [""],
                        category: p.category,
                        tags: (p.tags || []).join(", "),
                        stock: p.stock || 0,
                        isNew: !!p.isNew,
                        isFeatured: !!p.isFeatured,
                        variants: p.variants || [],
                        weight: p.weight ? String(p.weight) : "1",
                        dimLength: p.dimensions?.length ? String(p.dimensions.length) : "",
                        dimWidth: p.dimensions?.width ? String(p.dimensions.width) : "",
                        dimHeight: p.dimensions?.height ? String(p.dimensions.height) : "",
                    });
                }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [isEdit, productId]);

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const setImage = (i, v) => {
        setForm((s) => {
            const imgs = [...s.images];
            imgs[i] = v;
            return { ...s, images: imgs };
        });
    };
    const addImage = () => {
        if (form.images.length >= 6) return;
        setForm((s) => ({ ...s, images: [...s.images, ""] }));
    };
    const removeImage = (i) =>
        setForm((s) => ({
            ...s,
            images: s.images.filter((_, idx) => idx !== i),
        }));

    const addVariant = () => {
        if (form.variants.length >= 3) return;
        setForm((s) => ({
            ...s,
            variants: [...s.variants, { name: "Talla", options: [""] }],
        }));
    };
    const removeVariant = (i) =>
        setForm((s) => ({
            ...s,
            variants: s.variants.filter((_, idx) => idx !== i),
        }));
    const setVariantName = (i, v) =>
        setForm((s) => ({
            ...s,
            variants: s.variants.map((x, idx) =>
                idx === i ? { ...x, name: v } : x
            ),
        }));
    const setVariantOptions = (i, v) =>
        setForm((s) => ({
            ...s,
            variants: s.variants.map((x, idx) =>
                idx === i
                    ? {
                          ...x,
                          options: v.split(",").map((o) => o.trim()).filter(Boolean),
                      }
                    : x
            ),
        }));

    const validate = () => {
        const e = {};
        if (form.name.trim().length < 2) e.name = "Mínimo 2 caracteres.";
        if (form.shortDescription.trim().length < 4)
            e.shortDescription = "Cuéntanos brevemente.";
        if (form.description.trim().length < 10)
            e.description = "Mínimo 10 caracteres.";
        const price = parseFloat(form.price);
        if (!price || price <= 0) e.price = "Precio inválido.";
        if (form.originalPrice) {
            const op = parseFloat(form.originalPrice);
            if (!op || op <= price) e.originalPrice = "Debe ser mayor al precio.";
        }
        const validImages = form.images.filter((x) => x && x.trim());
        if (validImages.length === 0) e.images = "Agrega al menos una imagen.";
        if (form.stock < 0) e.stock = "El stock no puede ser negativo.";
        const weight = parseFloat(form.weight);
        if (!weight || weight <= 0) e.weight = "Peso inválido (kg).";
        // Dimensions are all-or-nothing — if any is set, all three required.
        const dims = [form.dimLength, form.dimWidth, form.dimHeight];
        const filledDims = dims.filter((d) => d && d.trim()).length;
        if (filledDims > 0 && filledDims < 3) {
            e.dimensions = "Si pones medidas, pon las 3 (largo, ancho, alto).";
        }
        return e;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length) {
            toast.error("Revisa los campos marcados.");
            return;
        }
        setSaving(true);
        setServerError(null);
        const validImages = form.images.filter((x) => x && x.trim());
        const variants = form.variants
            .filter((v) => v.name && v.options.length)
            .map((v) => ({ name: v.name, options: v.options }));
        const hasDims =
            form.dimLength && form.dimWidth && form.dimHeight;
        const payload = {
            name: form.name.trim(),
            slug: form.slug.trim() || undefined,
            shortDescription: form.shortDescription.trim(),
            description: form.description.trim(),
            price: parseFloat(form.price),
            originalPrice: form.originalPrice ? parseFloat(form.originalPrice) : null,
            images: validImages,
            category: form.category,
            tags: form.tags
                .split(",")
                .map((t) => t.trim().toLowerCase())
                .filter(Boolean),
            stock: parseInt(form.stock, 10) || 0,
            isNew: form.isNew,
            isFeatured: form.isFeatured,
            variants: variants.length ? variants : null,
            weight: parseFloat(form.weight) || 1.0,
            dimensions: hasDims
                ? {
                      length: parseFloat(form.dimLength),
                      width: parseFloat(form.dimWidth),
                      height: parseFloat(form.dimHeight),
                  }
                : null,
        };
        try {
            // El estado real lo decide el servidor (según la moderación), así
            // que el aviso sale de lo que responde, no de lo que suponemos.
            const { data } = isEdit
                ? await apiFetch.put(`/merchant/products/${productId}`, payload)
                : await apiFetch.post("/merchant/products", payload);

            if (data?.status === "pending") {
                toast.success(
                    isEdit ? "Cambios enviados a revisión" : "Producto enviado a revisión",
                    {
                        description:
                            "Lo revisamos y te avisamos en cuanto quede publicado.",
                    }
                );
            } else {
                toast.success(isEdit ? "Producto actualizado" : "Producto publicado", {
                    description: "Ya está disponible en tu tienda.",
                });
            }
            navigate("/dashboard/products");
        } catch (e) {
            const detail = e.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : "No se pudo guardar.";
            setServerError(msg);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="h-64 rounded-xl bg-zinc-100 animate-pulse" />;
    }

    return (
        <div data-testid="merchant-product-edit" className="space-y-6 max-w-3xl">
            <button
                onClick={() => navigate("/dashboard/products")}
                className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900"
            >
                <ArrowLeft className="w-4 h-4" /> Volver a productos
            </button>

            <header>
                <h1 className="text-3xl font-bold tracking-tight text-zinc-900">
                    {isEdit ? "Editar producto" : "Nuevo producto"}
                </h1>
                {requiresApproval && (
                    <div className="mt-4 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                        <Clock
                            className="w-5 h-5 text-amber-700 shrink-0 mt-0.5"
                            aria-hidden="true"
                        />
                        <div>
                            <p className="text-sm font-medium text-amber-900">
                                {isEdit
                                    ? "Los cambios pasan por revisión"
                                    : "Tu producto pasa por revisión antes de publicarse"}
                            </p>
                            <p className="mt-0.5 text-sm text-amber-800">
                                {isEdit
                                    ? "Si editas el nombre, la descripción, las fotos, la categoría o las etiquetas, el producto vuelve a revisión. Cambiar precio o inventario no lo saca de tu tienda."
                                    : "Lo revisamos y lo publicamos en tu tienda en cuanto quede aprobado. Mientras tanto lo verás como «En revisión» en tu lista."}
                            </p>
                        </div>
                    </div>
                )}
            </header>

            <form
                onSubmit={onSubmit}
                noValidate
                className="bg-white rounded-2xl border border-zinc-200 p-6 sm:p-8 space-y-6"
            >
                <div>
                    <Label className="text-xs font-medium text-zinc-700">
                        Imágenes * (máximo 6)
                    </Label>
                    <div className="mt-3 space-y-3">
                        {form.images.map((img, i) => (
                            <div key={i} className="flex gap-2 items-start">
                                <div className="flex-1">
                                    <ImageInput
                                        label={`Imagen ${i + 1}`}
                                        value={img}
                                        onChange={(v) => setImage(i, v)}
                                        aspect="aspect-square"
                                        testid={`product-image-${i}`}
                                    />
                                </div>
                                {form.images.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeImage(i)}
                                        className="mt-7 text-zinc-400 hover:text-red-600"
                                        aria-label="Quitar"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        ))}
                        {form.images.length < 6 && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={addImage}
                                className="border-zinc-200 border-dashed h-10"
                                data-testid="product-add-image"
                            >
                                <Plus className="w-4 h-4 mr-1" />
                                Agregar otra imagen
                            </Button>
                        )}
                    </div>
                    {errors.images && (
                        <p className="mt-1 text-xs text-red-600">{errors.images}</p>
                    )}
                </div>

                <Field label="Nombre del producto *" error={errors.name}>
                    <Input
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        placeholder="Rebozo Atardecer"
                        data-testid="product-name-input"
                        className="h-11"
                    />
                </Field>

                <Field label="Descripción corta *" error={errors.shortDescription}>
                    <Input
                        value={form.shortDescription}
                        onChange={(e) => set("shortDescription", e.target.value)}
                        placeholder="Tejido a mano en lana merino"
                        data-testid="product-short-desc-input"
                        className="h-11"
                        maxLength={200}
                    />
                </Field>

                <Field label="Descripción larga *" error={errors.description}>
                    <Textarea
                        value={form.description}
                        onChange={(e) => set("description", e.target.value)}
                        placeholder="Detalles, materiales, dimensiones..."
                        data-testid="product-description-input"
                        rows={5}
                    />
                </Field>

                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Precio MXN *" error={errors.price}>
                        <Input
                            type="number"
                            min="1"
                            step="1"
                            value={form.price}
                            onChange={(e) => set("price", e.target.value)}
                            placeholder="990"
                            data-testid="product-price-input"
                            className="h-11"
                        />
                    </Field>
                    <Field
                        label="Precio original (opcional)"
                        error={errors.originalPrice}
                        hint="Si está en oferta, muestra el precio antes del descuento."
                    >
                        <Input
                            type="number"
                            min="1"
                            step="1"
                            value={form.originalPrice}
                            onChange={(e) => set("originalPrice", e.target.value)}
                            placeholder="1290"
                            data-testid="product-original-price-input"
                            className="h-11"
                        />
                    </Field>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Categoría *">
                        <Select
                            value={form.category}
                            onValueChange={(v) => set("category", v)}
                        >
                            <SelectTrigger
                                data-testid="product-category-select"
                                className="h-11"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {categories.map((c) => (
                                    <SelectItem key={c.slug} value={c.slug} className="capitalize">
                                        {c.name || c.slug}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Stock disponible *" error={errors.stock}>
                        <Input
                            type="number"
                            min="0"
                            value={form.stock}
                            onChange={(e) => set("stock", e.target.value)}
                            data-testid="product-stock-input"
                            className="h-11"
                        />
                    </Field>
                </div>

                <Field
                    label="Tags (separados por coma)"
                    hint="Ayudan a que tu producto aparezca en búsquedas."
                >
                    <Input
                        value={form.tags}
                        onChange={(e) => set("tags", e.target.value)}
                        placeholder="hecho a mano, lana, oaxaca"
                        data-testid="product-tags-input"
                        className="h-11"
                    />
                </Field>

                {/* Datos de envío */}
                <div className="rounded-xl border border-zinc-200 bg-white p-4">
                    <div className="flex items-baseline justify-between">
                        <Label className="text-sm font-semibold text-zinc-900">
                            Datos para envío
                        </Label>
                        <span className="text-xs text-zinc-400">
                            * obligatorio
                        </span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">
                        Usados para cotizar el envío con la paquetería. Si dejas
                        el peso vacío usamos 1 kg como default.
                    </p>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
                        <Field label="Peso (kg) *" error={errors.weight}>
                            <Input
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={form.weight}
                                onChange={(e) => set("weight", e.target.value)}
                                placeholder="1.0"
                                data-testid="product-weight-input"
                                className="h-11"
                            />
                        </Field>
                        <Field label="Largo (cm)">
                            <Input
                                type="number"
                                min="1"
                                step="1"
                                value={form.dimLength}
                                onChange={(e) => set("dimLength", e.target.value)}
                                placeholder="15"
                                data-testid="product-dim-length"
                                className="h-11"
                            />
                        </Field>
                        <Field label="Ancho (cm)">
                            <Input
                                type="number"
                                min="1"
                                step="1"
                                value={form.dimWidth}
                                onChange={(e) => set("dimWidth", e.target.value)}
                                placeholder="15"
                                data-testid="product-dim-width"
                                className="h-11"
                            />
                        </Field>
                        <Field label="Alto (cm)">
                            <Input
                                type="number"
                                min="1"
                                step="1"
                                value={form.dimHeight}
                                onChange={(e) => set("dimHeight", e.target.value)}
                                placeholder="15"
                                data-testid="product-dim-height"
                                className="h-11"
                            />
                        </Field>
                    </div>
                    {errors.dimensions && (
                        <p className="mt-2 text-xs text-red-600">
                            {errors.dimensions}
                        </p>
                    )}
                </div>

                {/* Variants */}
                <div>
                    <Label className="text-xs font-medium text-zinc-700">
                        Variantes (opcional)
                    </Label>
                    <p className="text-xs text-zinc-500 mt-1">
                        Ej: Talla, Color. Las opciones se separan con coma.
                    </p>
                    <div className="mt-3 space-y-3">
                        {form.variants.map((v, i) => (
                            <div
                                key={i}
                                className="flex gap-2 items-start p-3 rounded-lg bg-zinc-50 border border-zinc-200"
                            >
                                <div className="grid grid-cols-3 gap-2 flex-1">
                                    <Input
                                        value={v.name}
                                        onChange={(e) => setVariantName(i, e.target.value)}
                                        placeholder="Talla"
                                        data-testid={`variant-name-${i}`}
                                        className="h-10"
                                    />
                                    <Input
                                        value={v.options.join(", ")}
                                        onChange={(e) =>
                                            setVariantOptions(i, e.target.value)
                                        }
                                        placeholder="S, M, L"
                                        data-testid={`variant-options-${i}`}
                                        className="h-10 col-span-2"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeVariant(i)}
                                    className="mt-2 text-zinc-400 hover:text-red-600"
                                    aria-label="Quitar variante"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                        {form.variants.length < 3 && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={addVariant}
                                className="border-zinc-200 border-dashed h-10"
                                data-testid="product-add-variant"
                            >
                                <Plus className="w-4 h-4 mr-1" />
                                Agregar variante
                            </Button>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap gap-6">
                    <ToggleField
                        label="Mostrar como Nuevo"
                        checked={form.isNew}
                        onChange={(v) => set("isNew", v)}
                        testid="product-isnew"
                    />
                    <ToggleField
                        label="Destacar en mi tienda"
                        checked={form.isFeatured}
                        onChange={(v) => set("isFeatured", v)}
                        testid="product-isfeatured"
                    />
                </div>

                {serverError && (
                    <p
                        data-testid="product-edit-error"
                        className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2"
                    >
                        {serverError}
                    </p>
                )}

                <div className="pt-2 flex gap-3">
                    <Button
                        type="submit"
                        disabled={saving}
                        data-testid="product-save-button"
                        className="bg-primary hover:bg-primary-hover text-primary-foreground"
                    >
                        {saving ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                            <Save className="w-4 h-4 mr-1" />
                        )}
                        {isEdit
                            ? "Guardar cambios"
                            : requiresApproval
                              ? "Enviar a revisión"
                              : "Publicar producto"}
                    </Button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, error, hint, children }) {
    return (
        <div>
            <Label className="text-xs font-medium text-zinc-700">{label}</Label>
            <div className="mt-1.5">{children}</div>
            {hint && !error && <p className="mt-1 text-xs text-zinc-600">{hint}</p>}
            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </div>
    );
}

function ToggleField({ label, checked, onChange, testid }) {
    return (
        <label className="flex items-center gap-2 cursor-pointer">
            <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
            <span className="text-sm text-zinc-700">{label}</span>
        </label>
    );
}
